/**
 * Boundary of the content screens (T09-26/27/28) to cms-service: pages, shop rules pages
 * (policies), menus, redirects, announcements, translations, website texts and languages.
 * Screens only talk to this interface — tests inject an in-memory fake, the app uses
 * `createContentApi` over the generated OpenAPI client (`@tcg/api-client`, cms T09-19).
 * Pickers (products, categories, pages, pictures) reuse the theme editor's `PickerApi`.
 */
import { type CmsPaths, createCmsClient, createTaxClient, idempotencyKey } from "@tcg/api-client";

import {
  ApiError,
  createThemeEditorApi,
  type PickerApi,
  type ThemeEditorApi,
  type ThemeEditorApiOptions,
} from "../../theme-editor/api/editorApi";

type Method = "get" | "post" | "put" | "patch" | "delete";

/** JSON body of the success answer (200, else 201, else 202) of `method path`. */
type JsonOf<P extends keyof CmsPaths, M extends Method> =
  NonNullable<CmsPaths[P][M]> extends { responses: infer R }
    ? R extends { 200: { content: { "application/json": infer B } } }
      ? B
      : R extends { 201: { content: { "application/json": infer B } } }
        ? B
        : never
    : never;

/** JSON request body of `method path`. */
type BodyOf<P extends keyof CmsPaths, M extends Method> =
  NonNullable<CmsPaths[P][M]> extends {
    requestBody?: { content: { "application/json": infer B } };
  }
    ? B
    : never;

// ---- pages (T09-07) -----------------------------------------------------------------------------
export type PageListOut = JsonOf<"/v1/admin/pages", "get">;
export type PageSummaryOut = PageListOut["data"][number];
export type PageOut = JsonOf<"/v1/admin/pages/{page_id}", "get">;
export type PageCreateIn = BodyOf<"/v1/admin/pages", "post">;
export type PagePatchIn = BodyOf<"/v1/admin/pages/{page_id}", "patch">;
export type PageStatus = PageSummaryOut["status"];
export type ContentWarningOut = NonNullable<PageOut["warnings"]>[number];

// ---- policies (T09-08) --------------------------------------------------------------------------
export type PolicyListOut = JsonOf<"/v1/admin/policies", "get">;
export type PolicySummaryOut = PolicyListOut["data"][number];
export type PolicyOut = JsonOf<"/v1/admin/policies/{policy_type}", "get">;
export type PolicyType = PolicyOut["type"];
export type PolicyPutIn = BodyOf<"/v1/admin/policies/{policy_type}", "put">;
export type PolicyFieldSpecOut = PolicyOut["fieldSpecs"][number];
export type PolicyPublishOut = JsonOf<"/v1/admin/policies/{policy_type}/publish", "post">;
export type PolicySuggestionsOut = JsonOf<"/v1/admin/policies/{policy_type}/suggestions", "get">;
export type PolicyVersionOut = JsonOf<
  "/v1/admin/policies/{policy_type}/versions",
  "get"
>["data"][number];
export type CompletenessOut = PolicyOut["completeness"];
export type MissingFieldOut = CompletenessOut["missing"][number];

// ---- menus (T09-09) -----------------------------------------------------------------------------
export type MenuSummaryOut = JsonOf<"/v1/admin/menus", "get">["data"][number];
export type MenuOut = JsonOf<"/v1/admin/menus/{menu_id}", "get">;
export type MenuItemOut = MenuOut["items"][number];
export type MenuLink = MenuItemOut["link"];
export type MenuTreeIn = BodyOf<"/v1/admin/menus/{menu_id}/items", "patch">;
export type MenuItemIn = NonNullable<MenuTreeIn["items"]>[number];
export type MenuCreateIn = BodyOf<"/v1/admin/menus", "post">;
export type MenuPatchIn = BodyOf<"/v1/admin/menus/{menu_id}", "patch">;

// ---- redirects (T09-10) -------------------------------------------------------------------------
export type RedirectListOut = JsonOf<"/v1/admin/redirects", "get">;
export type RedirectOut = RedirectListOut["data"][number];
export type RedirectCreateIn = BodyOf<"/v1/admin/redirects", "post">;
export type RedirectPatchIn = BodyOf<"/v1/admin/redirects/{redirect_id}", "patch">;
export type ImportReportOut = JsonOf<"/v1/admin/redirects/import", "post">;
export type NotFoundListOut = JsonOf<"/v1/admin/redirects/not-found", "get">;
export type NotFoundOut = NotFoundListOut["data"][number];
export interface RedirectQuery {
  q?: string;
  source?: "manual" | "auto" | "import";
  cursor?: string;
  limit?: number;
}

// ---- announcements (T09-11) ---------------------------------------------------------------------
export type AnnouncementOut = JsonOf<"/v1/admin/announcements/{announcement_id}", "get">;
export type AnnouncementIn = BodyOf<"/v1/admin/announcements", "post">;
export type AnnouncementPatchIn = BodyOf<"/v1/admin/announcements/{announcement_id}", "patch">;
export type AnnouncementState = AnnouncementOut["state"];

// ---- translations (T09-13) + website texts / languages (T09-12) ---------------------------------
export type TranslationListOut = JsonOf<"/v1/admin/translations", "get">;
export type TranslationRowOut = TranslationListOut["data"][number];
export type TranslationResourceType = TranslationRowOut["resourceType"];
export type TranslationItemIn = BodyOf<"/v1/admin/translations", "put">["items"][number];
export type TranslationSaveOut = JsonOf<"/v1/admin/translations", "put">;
export type TranslationSummaryListOut = JsonOf<"/v1/admin/translations/summary", "get">;
export type TranslationSummaryOut = TranslationSummaryListOut["data"][number];
export type TranslationImportOut = JsonOf<"/v1/admin/translations/import", "post">;
export interface TranslationQuery {
  resource?: TranslationResourceType[];
  locale?: string;
  missing?: boolean;
  q?: string;
  themeId?: string;
  cursor?: string;
  limit?: number;
}

export type UiStringListOut = JsonOf<"/v1/admin/ui-strings", "get">;
export type UiStringKeyOut = UiStringListOut["data"][number];
export type UiStringWriteOut = JsonOf<"/v1/admin/ui-strings", "put">;
export type UiStringProblemOut = NonNullable<UiStringWriteOut["warnings"]>[number];
export interface UiStringItemIn {
  ns: string;
  key: string;
  locale: string;
  value: string | null;
}
export interface UiStringQuery {
  ns?: string;
  locale?: string;
  q?: string;
  missing?: boolean;
  cursor?: string;
  limit?: number;
}

export type LocaleListOut = JsonOf<"/v1/admin/locales", "get">;
export type LocaleOut = LocaleListOut["data"][number];
export type LocalizationOut = LocaleListOut["localization"];
export type DetectionMode = LocalizationOut["detectionMode"];
export type LocaleFieldsIn = BodyOf<"/v1/admin/locales/{code}", "patch">;

export type ThemeSummary = Awaited<ReturnType<ThemeEditorApi["listThemes"]>>[number];

export interface ContentApi extends PickerApi {
  // pages
  listPages(query: {
    status?: PageStatus[];
    q?: string;
    missingLocale?: string;
    cursor?: string;
    limit?: number;
  }): Promise<PageListOut>;
  getPage(pageId: string): Promise<PageOut>;
  createPage(body: PageCreateIn): Promise<PageOut>;
  /** Per-language merge with `If-Match` = the page version (409 when someone saved first). */
  patchPage(pageId: string, patch: PagePatchIn, version: number): Promise<PageOut>;
  /** Now, or at `publishAt` (RFC 3339 with offset → "scheduled"). Idempotency-Key per call. */
  /** `idempotencyKey`: one per publish intent (a retry after a lost answer replays). */
  publishPage(pageId: string, publishAt?: string | null, idempotencyKey?: string): Promise<PageOut>;
  unpublishPage(pageId: string): Promise<PageOut>;
  /** Moves the page to the bin (restorable). */
  trashPage(pageId: string): Promise<void>;
  restorePage(pageId: string): Promise<PageOut>;
  // policies
  listPolicies(): Promise<PolicySummaryOut[]>;
  getPolicy(type: PolicyType): Promise<PolicyOut>;
  putPolicy(type: PolicyType, body: PolicyPutIn, version: number | null): Promise<PolicyOut>;
  /** 422 `policy_incomplete` (details = missing field × language) when a required field is empty. */
  publishPolicy(
    type: PolicyType,
    note?: string | null,
    idempotencyKey?: string,
  ): Promise<PolicyPublishOut>;
  policySuggestions(type: PolicyType): Promise<PolicySuggestionsOut>;
  listPolicyVersions(type: PolicyType): Promise<PolicyVersionOut[]>;
  // menus
  listMenus(): Promise<MenuSummaryOut[]>;
  getMenu(menuId: string): Promise<MenuOut>;
  createMenu(body: MenuCreateIn): Promise<MenuOut>;
  patchMenu(menuId: string, patch: MenuPatchIn, version: number): Promise<MenuOut>;
  /** Replace the whole item tree (ids kept) with `If-Match` = the menu version. */
  saveMenuTree(menuId: string, items: MenuItemIn[], version: number): Promise<MenuOut>;
  deleteMenu(menuId: string): Promise<void>;
  // redirects
  listRedirects(query: RedirectQuery): Promise<RedirectListOut>;
  createRedirect(body: RedirectCreateIn): Promise<RedirectOut>;
  /** `version` = the redirect's `version` (If-Match; 409 when someone changed it first). */
  patchRedirect(redirectId: string, patch: RedirectPatchIn, version: number): Promise<RedirectOut>;
  deleteRedirects(ids: string[]): Promise<number>;
  /** The whole table as CSV text (`from,to,status`). */
  exportRedirects(): Promise<string>;
  importRedirects(
    csv: string,
    dryRun: boolean,
    filename?: string,
    idempotencyKey?: string,
  ): Promise<ImportReportOut>;
  /** Progress of a large import that runs as a job (`importId` of the 202 answer). */
  getRedirectImport(importId: string): Promise<ImportReportOut>;
  listNotFound(query: { cursor?: string; limit?: number }): Promise<NotFoundListOut>;
  dismissNotFound(hitId: string): Promise<void>;
  // announcements
  listAnnouncements(): Promise<AnnouncementOut[]>;
  createAnnouncement(body: AnnouncementIn): Promise<AnnouncementOut>;
  patchAnnouncement(
    announcementId: string,
    patch: AnnouncementPatchIn,
    version: number,
  ): Promise<AnnouncementOut>;
  deleteAnnouncement(announcementId: string): Promise<void>;
  // translations
  listTranslations(query: TranslationQuery): Promise<TranslationListOut>;
  /** All or nothing — one PUT for the whole batch. */
  saveTranslations(items: TranslationItemIn[], themeId?: string): Promise<TranslationSaveOut>;
  translationSummary(themeId?: string): Promise<TranslationSummaryListOut>;
  exportTranslations(locale?: string, themeId?: string): Promise<string>;
  importTranslations(
    csv: string,
    dryRun: boolean,
    themeId?: string,
    idempotencyKey?: string,
  ): Promise<TranslationImportOut>;
  /** Machine drafts; throws `ApiError` 501 when not switched on (T09-13 stub). */
  suggestTranslations(
    items: Array<{ sourceValue: string; sourceLocale: string; targetLocale: string }>,
  ): Promise<string[]>;
  // website texts
  listUiStrings(query: UiStringQuery): Promise<UiStringListOut>;
  saveUiStrings(items: UiStringItemIn[]): Promise<UiStringWriteOut>;
  exportUiStrings(locale?: string): Promise<string>;
  // languages
  listLocales(): Promise<LocaleListOut>;
  /** `If-Match` = the localization version (one counter for every language setting). */
  patchLocale(code: string, fields: LocaleFieldsIn, version: number): Promise<LocaleOut>;
  patchLocalization(
    patch: { detectionMode?: DetectionMode; defaultLocale?: string },
    version: number,
  ): Promise<LocalizationOut>;
  // preview of drafts on the real storefront (T09-05 token of the live design)
  listThemes(): Promise<ThemeSummary[]>;
  /** Colour schemes of the live design (announcement colours). */
  getThemeSettings: ThemeEditorApi["getThemeSettings"];
  /** Page layouts of a design (`page`, `page.contact` …) — the "kind of page" choice. */
  listTemplates: ThemeEditorApi["listTemplates"];
  createPreviewToken: ThemeEditorApi["createPreviewToken"];
  revokePreviewToken: ThemeEditorApi["revokePreviewToken"];
  /** Active markets of tax-service (`GET /v1/admin/markets`, needs `tax.read`) — who sees an
   * announcement. Throws when the list cannot be read (the screen then uses its fallback). */
  listMarkets(): Promise<MarketChoice[]>;
}

/** A market an announcement can target: code + name per language (tax-service owns them). */
export interface MarketChoice {
  code: string;
  name: Record<string, string>;
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

function errorOf(result: { error?: unknown; response: Response }): ApiError {
  const body = result.error as { error?: { code?: unknown; details?: unknown } } | undefined;
  const code =
    typeof body?.error?.code === "string"
      ? body.error.code
      : `http_${String(result.response.status)}`;
  const details = Array.isArray(body?.error?.details)
    ? (body.error.details as Array<Record<string, unknown>>)
    : [];
  return new ApiError(result.response.status, code, details);
}

async function send<T>(request: Promise<FetchResult<T>>): Promise<FetchResult<T>> {
  try {
    return await request;
  } catch (error: unknown) {
    throw new ApiError(0, "network_error", [
      { message: error instanceof Error ? error.message : String(error) },
    ]);
  }
}

async function unwrap<T>(request: Promise<FetchResult<T>>): Promise<T> {
  const result = await send(request);
  if (result.data === undefined || !result.response.ok) {
    throw errorOf(result);
  }
  return result.data;
}

async function noContent(request: Promise<FetchResult<unknown>>): Promise<void> {
  const result = await send(request);
  if (!result.response.ok) {
    throw errorOf(result);
  }
}

const ifMatch = (version: number | null) =>
  version === null ? {} : { "If-Match": `"${String(version)}"` };

const csvBody = { bodySerializer: (body: unknown) => String(body) } as const;

/** The real implementation over the generated cms client. */
export function createContentApi(options: ThemeEditorApiOptions): ContentApi {
  const common = {
    baseUrl: options.baseUrl,
    getAccessToken: options.getAccessToken,
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  };
  const cms = createCmsClient(common);
  const tax = createTaxClient(common);
  const editor = createThemeEditorApi(options);
  const page = (pageId: string) => ({ page_id: pageId });
  const policy = (type: PolicyType) => ({ policy_type: type });
  const menu = (menuId: string) => ({ menu_id: menuId });

  return {
    searchPicker: editor.searchPicker,
    describeRefs: editor.describeRefs,
    listEnabledLocales: editor.listEnabledLocales,
    suggestTranslation: editor.suggestTranslation,
    listThemes: editor.listThemes,
    listTemplates: editor.listTemplates,
    getThemeSettings: editor.getThemeSettings,
    createPreviewToken: editor.createPreviewToken,
    revokePreviewToken: editor.revokePreviewToken,
    listMarkets: async () =>
      (await unwrap(tax.GET("/v1/admin/markets"))).data
        .filter((market) => market.active)
        .sort((a, b) => a.position - b.position)
        .map((market) => ({ code: market.code, name: { ...market.name } })),

    // ---- pages --------------------------------------------------------------------------------
    listPages: (query) =>
      unwrap(
        cms.GET("/v1/admin/pages", {
          params: {
            query: {
              status: query.status,
              q: query.q || undefined,
              missingLocale: query.missingLocale,
              cursor: query.cursor,
              limit: query.limit ?? 50,
            },
          },
        }),
      ),
    getPage: (pageId) =>
      unwrap(cms.GET("/v1/admin/pages/{page_id}", { params: { path: page(pageId) } })),
    createPage: (body) => unwrap(cms.POST("/v1/admin/pages", { body })),
    patchPage: (pageId, patch, version) =>
      unwrap(
        cms.PATCH("/v1/admin/pages/{page_id}", {
          params: { path: page(pageId), header: ifMatch(version) },
          body: patch,
        }),
      ),
    publishPage: async (pageId, publishAt, key) => {
      const result = await send(
        cms.POST("/v1/admin/pages/{page_id}/publish", {
          params: { path: page(pageId), header: { "Idempotency-Key": key ?? idempotencyKey() } },
          body: publishAt ? { publishAt } : {},
        }),
      );
      // 200 published / 202 scheduled — both carry the page
      if (!result.response.ok || result.data === undefined) {
        throw errorOf(result);
      }
      return result.data as PageOut;
    },
    unpublishPage: (pageId) =>
      unwrap(cms.POST("/v1/admin/pages/{page_id}/unpublish", { params: { path: page(pageId) } })),
    trashPage: (pageId) =>
      noContent(cms.DELETE("/v1/admin/pages/{page_id}", { params: { path: page(pageId) } })),
    restorePage: (pageId) =>
      unwrap(cms.POST("/v1/admin/pages/{page_id}/restore", { params: { path: page(pageId) } })),

    // ---- policies -----------------------------------------------------------------------------
    listPolicies: async () => (await unwrap(cms.GET("/v1/admin/policies"))).data,
    getPolicy: (type) =>
      unwrap(cms.GET("/v1/admin/policies/{policy_type}", { params: { path: policy(type) } })),
    putPolicy: (type, body, version) =>
      unwrap(
        cms.PUT("/v1/admin/policies/{policy_type}", {
          params: { path: policy(type), header: ifMatch(version) },
          body,
        }),
      ),
    publishPolicy: async (type, note, key) => {
      const result = await send(
        cms.POST("/v1/admin/policies/{policy_type}/publish", {
          params: { path: policy(type), header: { "Idempotency-Key": key ?? idempotencyKey() } },
          body: note ? { note } : {},
        }),
      );
      if (!result.response.ok || result.data === undefined) {
        throw errorOf(result);
      }
      return result.data as PolicyPublishOut;
    },
    policySuggestions: (type) =>
      unwrap(
        cms.GET("/v1/admin/policies/{policy_type}/suggestions", { params: { path: policy(type) } }),
      ),
    listPolicyVersions: async (type) =>
      (
        await unwrap(
          cms.GET("/v1/admin/policies/{policy_type}/versions", {
            params: { path: policy(type), query: { limit: 50 } },
          }),
        )
      ).data,

    // ---- menus --------------------------------------------------------------------------------
    listMenus: async () => (await unwrap(cms.GET("/v1/admin/menus"))).data,
    getMenu: (menuId) =>
      unwrap(cms.GET("/v1/admin/menus/{menu_id}", { params: { path: menu(menuId) } })),
    createMenu: (body) => unwrap(cms.POST("/v1/admin/menus", { body })),
    patchMenu: (menuId, patch, version) =>
      unwrap(
        cms.PATCH("/v1/admin/menus/{menu_id}", {
          params: { path: menu(menuId), header: ifMatch(version) },
          body: patch,
        }),
      ),
    saveMenuTree: (menuId, items, version) =>
      unwrap(
        cms.PATCH("/v1/admin/menus/{menu_id}/items", {
          params: { path: menu(menuId), header: ifMatch(version) },
          body: { items },
        }),
      ),
    deleteMenu: (menuId) =>
      noContent(cms.DELETE("/v1/admin/menus/{menu_id}", { params: { path: menu(menuId) } })),

    // ---- redirects ----------------------------------------------------------------------------
    listRedirects: (query) =>
      unwrap(
        cms.GET("/v1/admin/redirects", {
          params: {
            query: {
              q: query.q || undefined,
              source: query.source,
              cursor: query.cursor,
              limit: query.limit ?? 100,
            },
          },
        }),
      ),
    createRedirect: (body) => unwrap(cms.POST("/v1/admin/redirects", { body })),
    patchRedirect: (redirectId, patch, version) =>
      unwrap(
        cms.PATCH("/v1/admin/redirects/{redirect_id}", {
          params: {
            path: { redirect_id: redirectId },
            header: { "If-Match": `"${String(version)}"` },
          },
          body: patch,
        }),
      ),
    deleteRedirects: async (ids) => {
      if (ids.length === 1 && ids[0]) {
        await noContent(
          cms.DELETE("/v1/admin/redirects/{redirect_id}", {
            params: { path: { redirect_id: ids[0] } },
          }),
        );
        return 1;
      }
      return (await unwrap(cms.POST("/v1/admin/redirects/bulk-delete", { body: { ids } }))).deleted;
    },
    exportRedirects: async () => {
      const result = await send(
        cms.GET("/v1/admin/redirects/export", {
          headers: { Accept: "text/csv" },
          parseAs: "text",
        }),
      );
      if (!result.response.ok) {
        throw errorOf(result);
      }
      return String(result.data ?? "");
    },
    importRedirects: (csv, dryRun, filename, key) =>
      unwrap(
        cms.POST("/v1/admin/redirects/import", {
          params: { query: { dryRun }, header: { "Idempotency-Key": key ?? idempotencyKey() } },
          body: filename ? { csv, filename } : { csv },
        }),
      ),
    getRedirectImport: (importId) =>
      unwrap(
        cms.GET("/v1/admin/redirects/imports/{import_id}", {
          params: { path: { import_id: importId } },
        }),
      ),
    listNotFound: (query) =>
      unwrap(
        cms.GET("/v1/admin/redirects/not-found", {
          params: { query: { sort: "count", cursor: query.cursor, limit: query.limit ?? 100 } },
        }),
      ),
    dismissNotFound: (hitId) =>
      noContent(
        cms.DELETE("/v1/admin/redirects/not-found/{hit_id}", {
          params: { path: { hit_id: hitId } },
        }),
      ),

    // ---- announcements ------------------------------------------------------------------------
    listAnnouncements: async () =>
      (await unwrap(cms.GET("/v1/admin/announcements", { params: { query: { limit: 200 } } })))
        .data,
    createAnnouncement: (body) => unwrap(cms.POST("/v1/admin/announcements", { body })),
    patchAnnouncement: (announcementId, patch, version) =>
      unwrap(
        cms.PATCH("/v1/admin/announcements/{announcement_id}", {
          params: { path: { announcement_id: announcementId }, header: ifMatch(version) },
          body: patch,
        }),
      ),
    deleteAnnouncement: (announcementId) =>
      noContent(
        cms.DELETE("/v1/admin/announcements/{announcement_id}", {
          params: { path: { announcement_id: announcementId } },
        }),
      ),

    // ---- translations -------------------------------------------------------------------------
    listTranslations: (query) =>
      unwrap(
        cms.GET("/v1/admin/translations", {
          params: {
            query: {
              resource: query.resource,
              locale: query.locale,
              missing: query.missing ?? false,
              q: query.q || undefined,
              themeId: query.themeId,
              cursor: query.cursor,
              limit: query.limit ?? 500,
            },
          },
        }),
      ),
    saveTranslations: (items, themeId) =>
      unwrap(
        cms.PUT("/v1/admin/translations", {
          params: { query: themeId ? { themeId } : {} },
          body: { items },
        }),
      ),
    translationSummary: (themeId) =>
      unwrap(
        cms.GET("/v1/admin/translations/summary", {
          params: { query: themeId ? { themeId } : {} },
        }),
      ),
    exportTranslations: async (locale, themeId) => {
      const result = await send(
        cms.GET("/v1/admin/translations/export", {
          params: { query: { format: "csv", locale, themeId } },
          headers: { Accept: "text/csv" },
          parseAs: "text",
        }),
      );
      if (!result.response.ok) {
        throw errorOf(result);
      }
      return String(result.data ?? "");
    },
    importTranslations: (csv, dryRun, themeId, key) =>
      unwrap(
        cms.POST("/v1/admin/translations/import", {
          params: {
            query: { dryRun, ...(themeId ? { themeId } : {}) },
            header: { "Idempotency-Key": key ?? idempotencyKey() },
          },
          body: csv as never,
          headers: { "Content-Type": "text/csv" },
          ...csvBody,
        }),
      ),
    suggestTranslations: async (items) =>
      (await unwrap(cms.POST("/v1/admin/translations/suggest", { body: { items } }))).data.map(
        (row) => row.value,
      ),

    // ---- website texts ------------------------------------------------------------------------
    listUiStrings: (query) =>
      unwrap(
        cms.GET("/v1/admin/ui-strings", {
          params: {
            query: {
              ns: query.ns,
              locale: query.locale,
              q: query.q || undefined,
              missing: query.missing ?? false,
              cursor: query.cursor,
              limit: query.limit ?? 500,
            },
          },
        }),
      ),
    saveUiStrings: (items) => unwrap(cms.PUT("/v1/admin/ui-strings", { body: { items } as never })),
    exportUiStrings: async (locale) => {
      const result = await send(
        cms.GET("/v1/admin/ui-strings/export", {
          params: { query: { format: "csv", locale } },
          headers: { Accept: "text/csv" },
          parseAs: "text",
        }),
      );
      if (!result.response.ok) {
        throw errorOf(result);
      }
      return String(result.data ?? "");
    },

    // ---- languages ----------------------------------------------------------------------------
    listLocales: () => unwrap(cms.GET("/v1/admin/locales")),
    patchLocale: (code, fields, version) =>
      unwrap(
        cms.PATCH("/v1/admin/locales/{code}", {
          params: { path: { code }, header: ifMatch(version) },
          body: fields,
        }),
      ),
    patchLocalization: (patch, version) =>
      unwrap(
        cms.PATCH("/v1/admin/localization", {
          params: { header: ifMatch(version) },
          body: patch,
        }),
      ),
  };
}

export { ApiError };
